import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Home, CreditCard, DollarSign, Users, Package, Mail, BarChart3, Building2, Globe } from 'lucide-react';
import { usePermissions } from '../../../contexts/PermissionContext';
import { useCompany } from '../../../contexts/CompanyContext';
import { PERMISSIONS } from '../../../constants/permissionMappings';
import CompanySwitcher from '../../company/CompanySwitcher';
import useCmsAccess from '../../../hooks/useCmsAccess';

/**
 * @description Permission-aware sidebar navigation for merchant users
 *
 * Key features:
 * - Dashboard is the only item visible before a company exists
 * - Other items use the same hasPermission filter as Reporting / Transactions
 * - Website is cms.view (not company.view) AND CMS feature entitlement
 *
 * @dependencies
 * - PermissionContext: hasPermission / isLoaded
 * - CompanyContext: hasCompany (pre-company nav)
 * - useCmsAccess: platform/SO CMS feature flag (AND, never OR-error)
 *
 * @notes
 * Profile badge "Merchant" is a Wristband/platform role — ignore it for nav.
 * Company Employees already have company.view (Company tab). Website uses
 * cms.view the same way Reporting uses reports.view_basic. CMS entitlement
 * is a second gate after RBAC — fetch errors fail closed. Balances stays
 * permission-free after a company exists but is hidden during onboarding.
 */
const MerchantSidebar = ({ isSidebarOpen, onLinkClick, isMobile = false }) => {
  const { hasPermission, isLoaded } = usePermissions();
  const { hasCompany } = useCompany();
  const { allowed: cmsAllowed, loading: cmsLoading } = useCmsAccess();

  const visibleLinks = useMemo(() => {
    const merchantLinks = [
      { to: '/merchant/dashboard', label: 'Dashboard', icon: Home, permission: null },
      { to: '/merchant/transactions', label: 'Transactions', icon: CreditCard, permission: PERMISSIONS.transactions.view },
      { to: '/merchant/balances', label: 'Balances', icon: DollarSign, permission: null },
      { to: '/merchant/customers', label: 'Customers', icon: Users, permission: PERMISSIONS.customers.view },
      { to: '/merchant/products', label: 'Products', icon: Package, permission: PERMISSIONS.products.view },
      { to: '/merchant/outreach', label: 'Outreach', icon: Mail, permission: PERMISSIONS.outreach.view },
      { to: '/merchant/reporting', label: 'Reporting', icon: BarChart3, permission: PERMISSIONS.reports.view_basic },
      { to: '/merchant/company', label: 'Company', icon: Building2, permission: PERMISSIONS.company.view },
      { to: '/merchant/website', label: 'Website', icon: Globe, permission: PERMISSIONS.cms.view },
    ];

    return merchantLinks.filter((link) => {
      if (link.permission === null) {
        if (link.to === '/merchant/dashboard') {
          return true;
        }
        return isLoaded && hasCompany;
      }

      if (!isLoaded || !hasCompany || !hasPermission(link.permission)) {
        return false;
      }

      if (link.to === '/merchant/website') {
        return !cmsLoading && cmsAllowed;
      }

      return true;
    });
  }, [isLoaded, hasPermission, hasCompany, cmsLoading, cmsAllowed]);

  return (
    <>
      <CompanySwitcher isSidebarOpen={isSidebarOpen} isMobile={isMobile} />

      {visibleLinks.map((link) => (
        <Link
          key={link.to}
          to={link.to}
          className="group relative flex items-center py-3 rounded-lg transition-colors duration-200 text-gray-300 hover:bg-gray-800 hover:text-white"
          onClick={() => isMobile && onLinkClick && onLinkClick()}
        >
          <div className="w-12 flex items-center justify-center flex-shrink-0">
            <link.icon className="w-5 h-5" />
          </div>

          <span className={`text-sm font-medium overflow-hidden whitespace-nowrap transition-all duration-300 ${
            isMobile ? 'opacity-100 max-w-full' : isSidebarOpen ? 'opacity-100 max-w-full delay-[50ms]' : 'opacity-0 max-w-0'
          }`}>
            {link.label}
          </span>

          {!isMobile && !isSidebarOpen && (
            <div className="absolute left-14 top-1/2 -translate-y-1/2 bg-gray-800 text-white px-2 py-1 rounded opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-50 pointer-events-none">
              {link.label}
            </div>
          )}
        </Link>
      ))}
    </>
  );
};

export default MerchantSidebar;
